import {
	type ReactNode,
	createContext,
	useCallback,
	useContext,
	useState,
} from "react";

export type ReplyTarget = {
	/** The actual comment ID (for highlight) */
	commentId: string;
	/** The parent comment ID for the mutation (data.parent?.id || data.id) */
	parentCommentId: string;
	/** Username for the @mention */
	authorUsername: string;
	/** Display name for the banner */
	authorDisplayName: string;
	/** Raw Tiptap JSON content for the banner preview */
	content: unknown;
	/** Relay connection IDs for all sort orders */
	connections: string[];
	/** Refetch callback to reload children after reply */
	refetch: () => void;
};

type MobileReplyContextType = {
	replyTarget: ReplyTarget | null;
	setReplyTarget: (target: ReplyTarget) => void;
	clearReplyTarget: () => void;
};

const MobileReplyContext = createContext<MobileReplyContextType>({
	replyTarget: null,
	setReplyTarget: () => {},
	clearReplyTarget: () => {},
});

export function MobileReplyProvider({ children }: { children: ReactNode }) {
	const [replyTarget, setReplyTargetState] = useState<ReplyTarget | null>(null);

	const setReplyTarget = useCallback((target: ReplyTarget) => {
		setReplyTargetState(target);
	}, []);

	const clearReplyTarget = useCallback(() => {
		setReplyTargetState(null);
	}, []);

	return (
		<MobileReplyContext.Provider
			value={{ replyTarget, setReplyTarget, clearReplyTarget }}
		>
			{children}
		</MobileReplyContext.Provider>
	);
}

export function useMobileReply() {
	return useContext(MobileReplyContext);
}
